import React, { Suspense, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Panel } from "react-bootstrap";
import { navigate } from "raviger";
import _ from "lodash";

import withAuthentication from "../../hoc/withAuthentication";
import withLayout from "../../hoc/withLayout";
import Permission from "../../components/Permission/Permission";

import { useAlertStore } from "../../store/providers/Alert";
import { useContractStore } from "../../store/providers/Contract";

import {
  breadcrumbAddRoute,
  breadcrumbRemoveRoute,
} from "../../store/actions/breadcrumb";

import Spinner from "../../components/Spinner/Spinner";

import * as constants from "../../constants";
import { sorting, columns } from "./ContractsSchema";

const BaseList = React.lazy(() =>
  import("../../components/Lists/BaseList/BaseList")
);
const SideMenu = React.lazy(() => import("../../components/SideMenu/SideMenu"));
const PageHeader = React.lazy(() =>
  import("../../components/PageHeader/PageHeader")
);

function Contracts() {
  const dispatch = useDispatch();

  const { addAlert } = useAlertStore();

  const { listRecords, deleteRecord, data, clearData } = useContractStore();
  const [listData, setListData] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const pagePermission = constants.PERMISSION_CONTRACTS;
  const [hasPermission, setHasPermission] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const selectedHandler = (contract) => {
    navigate(`/contracts/edit/${contract.id}`);
  };

  const addedHandler = () => {
    navigate(`/contracts/new`);
  };

  const deletedHandler = (contract) => {
    deleteRecord({ contractId: contract.id });
  };

  const refreshedHandler = () => {
    listRecords();
  };

  useEffect(() => {
    if (!hasPermission) {
      return;
    }
    if (isMounted) {
      return;
    }

    dispatch(
      breadcrumbAddRoute({
        key: "contracts",
        text: "Contracts",
        path: "/contracts",
      })
    );
    listRecords();

    setIsMounted(true);
  }, [isMounted, hasPermission, listRecords, dispatch]);

  useEffect(() => {
    if (!_.isEmpty(data)) {
      if ((data.status === "fail" || data.status === "error") && data.body) {
        addAlert({
          key: constants.ALERT_SERVER_ERROR,
          text: <strong>{data.body}</strong>,
          type: constants.ALERT_TYPE_DANGER,
        });
        clearData();
      }

      if (data.scope === "delete" && data.status === "success") {
        listRecords();
      }

      if (data.status === "success" && data.scope === "list") {
        setListData(data.body);
      }

      setIsBusy(
        data.status === "processing" &&
          (data.scope === "delete" || data.scope === "list")
      );
    }
  }, [data, addAlert, clearData, listRecords]);

  useEffect(() => {
    return () => {
      dispatch(breadcrumbRemoveRoute());
    };
  }, [dispatch]);

  return (
    <>
      <SideMenu activeKey="contracts" />
      <div className="content-body">
        <Permission
          showDenied={true}
          pagePermission={pagePermission}
          hasPermission={hasPermission}
          setHasPermission={setHasPermission}
          setCanEdit={setCanEdit}
        />
        {hasPermission && isMounted && (
          <Suspense fallback={<Spinner text="Loading Contracts..." />}>
            <div style={{ paddingRight: "15px" }}>
              <PageHeader title="Contracts" />
              <Panel>
                <Panel.Body>
                  <BaseList
                    compType="Contract"
                    listData={listData}
                    isBusy={isBusy}
                    sorting={sorting}
                    columns={columns}
                    hideCopy={true}
                    hidePrint={true}
                    activeValue="Active"
                    activeField="status"
                    permissionEdit={canEdit}
                    added={() => addedHandler()}
                    selected={(contract) => selectedHandler(contract)}
                    deleted={(contract) => deletedHandler(contract)}
                    refreshed={() => refreshedHandler()}
                  />
                </Panel.Body>
              </Panel>
            </div>
          </Suspense>
        )}
      </div>
    </>
  );
}

export default withAuthentication(withLayout(Contracts));
